import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { useRouter } from 'src/routes/hooks';

import { fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

import { getDeepLinkForNotification } from './notification-deep-link';

// Map notification types to icons
const NOTIFICATION_ICONS = {
  session: 'solar:calendar-bold-duotone',
  payment: 'solar:card-bold-duotone',
  booking: 'solar:ticket-bold-duotone',
  match: 'solar:gamepad-bold-duotone',
  profile: 'solar:user-bold-duotone',
  general: 'solar:bell-bold-duotone',
};

// Map notification types to colors
const NOTIFICATION_COLORS = {
  session: 'primary.main',
  payment: 'success.main',
  booking: 'info.main',
  match: 'warning.main',
  profile: 'secondary.main',
  general: 'text.secondary',
};

export function NotificationItem({ notification, onMarkAsRead, onDelete }) {
  const router = useRouter();
  const {
    type = 'general',
    title,
    message,
    category,
    isUnRead,
    createdAt,
    avatarUrl,
  } = notification;

  const icon = NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.general;
  const iconColor = NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.general;

  const handleClick = () => {
    if (isUnRead && onMarkAsRead) {
      onMarkAsRead();
    }
    const deepLink = getDeepLinkForNotification(notification);
    if (deepLink) {
      router.push(deepLink);
    }
  };

  const renderAvatar = (
    <ListItemAvatar>
      {avatarUrl ? (
        <Avatar src={avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'background.neutral',
          }}
        >
          <Iconify icon={icon} width={24} sx={{ color: iconColor }} />
        </Stack>
      )}
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      }
      secondary={
        <Stack spacing={0.5}>
          {message && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {message}
            </Typography>
          )}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: 'caption', color: 'text.disabled' }}
            divider={
              <Box
                sx={{
                  width: 2,
                  height: 2,
                  bgcolor: 'currentColor',
                  mx: 0.5,
                  borderRadius: '50%',
                }}
              />
            }
          >
            {fToNow(createdAt)}
            {category}
          </Stack>
        </Stack>
      }
    />
  );

  const renderUnReadBadge = isUnRead && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  const renderActions = (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        opacity: 0,
        transition: 'opacity 0.2s',
        '.MuiListItemButton-root:hover &': {
          opacity: 1,
        },
      }}
    >
      {isUnRead && onMarkAsRead && (
        <Tooltip title="Mark as read">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead();
            }}
          >
            <Iconify icon="eva:checkmark-fill" width={16} />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Iconify icon="eva:trash-2-outline" width={16} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      onClick={handleClick}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
        position: 'relative',
        bgcolor: isUnRead ? 'action.hover' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1, pr: 3 }}>{renderText}</Stack>

      {renderActions}
    </ListItemButton>
  );
}
